import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Tab,
  Td,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  Tr,
  useDisclosure,
  useToast,
  VStack
} from "@chakra-ui/react";
import { DeleteIcon, DownloadIcon, EditIcon } from "@chakra-ui/icons";
import { FaSitemap, FaRegFileAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { PLATFORM_ROLES, BU_ROLES } from "../../../constants/roles";
import {
  approveConsultantActivityReport,
  approveConsultantLeaveRequest,
  addConsultantComment,
  disableConsultantPortalAccess,
  confirmDeleteConsultant,
  deleteConsultant,
  downloadConsultantCv,
  getConsultant,
  getConsultantComments,
  inviteConsultantPortalAccess,
  listConsultantInternalActivityReports,
  listConsultantInternalAssignmentFeedback,
  listConsultantInternalLeaveRequests,
  rejectConsultantActivityReport,
  rejectConsultantLeaveRequest,
  resendConsultantPortalAccess,
} from "../../../api/consultantsApi";
import { listMatches, generateMatchesForConsultant } from "../../../api/matchesApi";
import { listMentionableUsers } from "../../../api/authApi";
import ThreadedComments from "../../../components/Portal/ThreadedComments";
import ConsultantFormModal from "./ConsultantFormModal";
import ConsultantAssessmentModal from "./ConsultantAssessmentModal";
import ConsultantConversationsTab from "./ConsultantConversationsTab";
import ConsultantSkillsPortfolioTab from "./ConsultantSkillsPortfolioTab";
import ConsultantSkillsTab from "./ConsultantSkillsTab";
import ConsultantAssignmentsTab from "./ConsultantAssignmentsTab";
import ConsultantFollowUpTab from "./ConsultantFollowUpTab";
import MatchDetailModal from "./MatchDetailModal";
import ConfirmDialog from "../../../components/Portal/ConfirmDialog";
import {
  CONSULTANT_STAFFING_STATUS_COLORS,
  CONSULTANT_STAFFING_STATUS_LABELS,
  CONSULTANT_STAFFING_STATUS_LABEL_KEYS,
  CONSULTANT_STATUS_LABELS,
  CONSULTANT_STATUS_LABEL_KEYS,
  CONSULTANT_LIFECYCLE_STATUS_COLORS,
  PORTAL_ACCESS_STATUS_COLORS,
  getStatusLabel
} from "../../../constants/statuses";
import { CONTRACT_TYPE_LABELS } from "../../../constants/needs";
import {
  Panel,
  DataTable,
  StatusBadge,
  EmptyState,
  FieldLabel
} from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX
} from "../../../components/Portal/portalModalChrome";

// Field value text inside detail Panels.
const FieldValue = ({ children, ...rest }) => (
  <Text
    fontSize="13px"
    fontWeight="600"
    color="portal.text"
    sx={{ marginBottom: "0 !important" }}
    {...rest}
  >
    {children}
  </Text>
);

// Pill skill tag (template `.chip`).
const SkillTag = ({ children, colorScheme }) => (
  <Badge
    colorScheme={colorScheme}
    variant="subtle"
    fontSize="11px"
    px="8px"
    py="2px"
    borderRadius="999px"
  >
    {children}
  </Badge>
);

const ConsultantDetailModal = ({
  isOpen,
  onClose,
  consultantId,
  navigateOnClose = true
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation("portal");
  const {
    effectiveRole,
    selectedBusinessUnitId,
    isLoaded: isBUContextLoaded,
    features
  } = useBusinessUnit();

  const [consultant, setConsultant] = useState(null);
  const [loadingConsultant, setLoadingConsultant] = useState(true);

  // Edit modal
  const editModal = useDisclosure();
  const deleteConfirmDialog = useDisclosure();
  const [deleteWarning, setDeleteWarning] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  // New modals
  const assessmentModal = useDisclosure();

  // Comments
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [mentionableUsers, setMentionableUsers] = useState([]);

  // Matches
  const [consultantMatches, setConsultantMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [matchFilter, setMatchFilter] = useState("all"); // "all" | "submitted"
  const matchDetailModal = useDisclosure();
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedNeed, setSelectedNeed] = useState(null);

  const [portalLoading, setPortalLoading] = useState(false);
  const [portalActionLoading, setPortalActionLoading] = useState(false);
  const [activityReports, setActivityReports] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [assignmentFeedback, setAssignmentFeedback] = useState([]);

  // Review decision (approve/reject) comment modal — replaces browser prompt
  const reviewCommentModal = useDisclosure();
  const [pendingReview, setPendingReview] = useState(null);
  const [reviewCommentText, setReviewCommentText] = useState("");
  const [reviewSubmitLoading, setReviewSubmitLoading] = useState(false);

  const canDelete = useMemo(() => {
    return (
      effectiveRole === PLATFORM_ROLES.COMPANY_ADMIN ||
      effectiveRole === BU_ROLES.ADMIN ||
      effectiveRole === BU_ROLES.MANAGER
    );
  }, [effectiveRole]);
  const canManagePortalAccess = useMemo(() => {
    return [
      PLATFORM_ROLES.COMPANY_ADMIN,
      BU_ROLES.ADMIN,
      BU_ROLES.MANAGER,
      BU_ROLES.RECRUITER,
      BU_ROLES.RECRUITMENT_MANAGER,
      BU_ROLES.HR,
      BU_ROLES.HR_MANAGER,
      BU_ROLES.SALES_MANAGER,
    ].includes(effectiveRole);
  }, [effectiveRole]);
  const canReviewActivityAndLeave = useMemo(() => {
    return [
      PLATFORM_ROLES.COMPANY_ADMIN,
      BU_ROLES.ADMIN,
      BU_ROLES.MANAGER,
      BU_ROLES.HR,
      BU_ROLES.HR_MANAGER,
    ].includes(effectiveRole);
  }, [effectiveRole]);
  const canViewAssignmentFeedback = useMemo(() => {
    return canReviewActivityAndLeave || effectiveRole === BU_ROLES.SALES_MANAGER;
  }, [canReviewActivityAndLeave, effectiveRole]);

  const hasCvModule = features?.has_cv;
  const hasSkillsModule = features?.has_skills;
  const fetchConsultant = useCallback(async () => {
    if (!consultantId) return;
    setLoadingConsultant(true);
    try {
      const res = await getConsultant(consultantId, {
        buId: selectedBusinessUnitId || undefined
      });
      setConsultant(res);
    } catch (e) {
      toast({
        title: t("pilotConsultants.detail.toasts.loadConsultantFailed"),
        status: "error"
      });
      setConsultant(null);
    } finally {
      setLoadingConsultant(false);
    }
  }, [consultantId, selectedBusinessUnitId, toast, t]);

  const fetchComments = useCallback(async () => {
    if (!consultantId) return;
    setLoadingComments(true);
    try {
      const res = await getConsultantComments(consultantId, {
        buId: selectedBusinessUnitId || undefined
      });
      setComments(res || []);
    } catch (e) {
      console.error("Failed to load comments", e);
    } finally {
      setLoadingComments(false);
    }
  }, [consultantId, selectedBusinessUnitId]);

  const fetchMentionableUsers = useCallback(async () => {
    try {
      const res = await listMentionableUsers({
        buId: selectedBusinessUnitId || undefined
      });
      setMentionableUsers(res || []);
    } catch (e) {
      console.error("Failed to load mentionable users", e);
    }
  }, [selectedBusinessUnitId]);

  const isMatchPositioned = useCallback((m) => {
    return Boolean(
      m?.positioned ??
        m?.isPositioned ??
        m?.is_positioned ??
        m?.salesValidated ??
        m?.sales_validated
    );
  }, []);

  const getMatchSubmissionStatus = useCallback((m) => {
    const status = m?.submissionStatus ?? m?.submission_status;
    return status || "draft";
  }, []);

  const getSubmissionBadgeColor = useCallback((status) => {
    if (status === "submitted_to_client") return "green";
    if (status === "rejected") return "red";
    if (status === "withdrawn") return "orange";
    return "gray";
  }, []);

  const getSubmissionStatusLabel = useCallback((status) => {
    if (status === "submitted_to_client") {
      return t("pilotMatches.detail.submission.status.submittedToClient", {
        defaultValue: "Submitted to client"
      });
    }
    if (status === "rejected") {
      return t("pilotMatches.detail.submission.status.rejected", {
        defaultValue: "Rejected by client"
      });
    }
    if (status === "withdrawn") {
      return t("pilotMatches.detail.submission.status.withdrawn", {
        defaultValue: "Withdrawn"
      });
    }
    return t("pilotMatches.detail.submission.status.draft", {
      defaultValue: "Draft"
    });
  }, [t]);

  const formatSubmittedAt = useCallback((v) => {
    if (!v) return null;
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleString();
    } catch {
      return null;
    }
  }, []);

  const fetchConsultantMatches = useCallback(async () => {
    if (!consultantId) return;
    setLoadingMatches(true);
    try {
      const res = await listMatches(
        { consultantId, page: 1, pageSize: 500 },
        { buId: selectedBusinessUnitId || undefined }
      );
      const items = Array.isArray(res?.items) ? res.items : [];
      const pairs = items.map((m) => {
        const need =
          m.need ||
          {
            id: m.needId ?? m.need_id,
            clientName: m.clientName ?? m.client_name ?? "",
            title: m.needTitle ?? m.need_title ?? ""
          };
        return { match: m, need };
      });
      setConsultantMatches(pairs);
    } catch (e) {
      console.error("Failed to fetch consultant matches", e);
      setConsultantMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  }, [consultantId, selectedBusinessUnitId]);

  const [generatingMatches, setGeneratingMatches] = useState(false);

  const handleGenerateMatches = useCallback(async () => {
    if (!consultantId) return;
    setGeneratingMatches(true);
    try {
      const items = await generateMatchesForConsultant(consultantId, {
        buId: selectedBusinessUnitId || undefined
      });
      const arr = Array.isArray(items) ? items : [];
      const pairs = arr.map((m) => {
        const need =
          m.need ||
          {
            id: m.needId ?? m.need_id,
            clientName: m.clientName ?? m.client_name ?? "",
            title: m.needTitle ?? m.need_title ?? ""
          };
        return { match: m, need };
      });
      setConsultantMatches(pairs);
      toast({
        title: t("pilotConsultants.detail.matches.generateSuccess", {
          defaultValue: `${arr.length} match(es) found`
        }),
        status: "success",
      });
    } catch (e) {
      console.error("Failed to generate matches for consultant", e);
      toast({
        title: t("pilotConsultants.detail.matches.generateError", { defaultValue: "Failed to generate matches" }),
        status: "error",
      });
    } finally {
      setGeneratingMatches(false);
    }
  }, [consultantId, selectedBusinessUnitId, toast, t]);

  const fetchPortalData = useCallback(async () => {
    if (!consultantId) return;
    setPortalLoading(true);
    try {
      const [reports, requests, feedback] = await Promise.all([
        listConsultantInternalActivityReports(consultantId, {
          buId: selectedBusinessUnitId || undefined,
        }).catch(() => []),
        listConsultantInternalLeaveRequests(consultantId, {
          buId: selectedBusinessUnitId || undefined,
        }).catch(() => []),
        listConsultantInternalAssignmentFeedback(consultantId, {
          buId: selectedBusinessUnitId || undefined,
        }).catch(() => []),
      ]);
      setActivityReports(Array.isArray(reports) ? reports : []);
      setLeaveRequests(Array.isArray(requests) ? requests : []);
      setAssignmentFeedback(Array.isArray(feedback) ? feedback : []);
    } finally {
      setPortalLoading(false);
    }
  }, [consultantId, selectedBusinessUnitId]);

  const handlePortalAction = useCallback(
    async (action) => {
      if (!consultantId) return;
      setPortalActionLoading(true);
      try {
        if (action === "invite") {
          await inviteConsultantPortalAccess(consultantId, {
            buId: selectedBusinessUnitId || undefined,
          });
          toast({ title: t("pilotConsultants.detail.consultantPortal.toasts.inviteSent"), status: "success" });
        } else if (action === "resend") {
          await resendConsultantPortalAccess(consultantId, {
            buId: selectedBusinessUnitId || undefined,
          });
          toast({ title: t("pilotConsultants.detail.consultantPortal.toasts.inviteResent"), status: "success" });
        } else if (action === "disable") {
          await disableConsultantPortalAccess(consultantId, {
            buId: selectedBusinessUnitId || undefined,
          });
          toast({ title: t("pilotConsultants.detail.consultantPortal.toasts.accessDisabled"), status: "success" });
        }
        await fetchConsultant();
        await fetchPortalData();
      } catch (error) {
        toast({
          title: t("pilotConsultants.detail.consultantPortal.toasts.actionFailedTitle"),
          description: error?.response?.data?.detail || t("pilotConsultants.detail.consultantPortal.toasts.actionFailedDescription"),
          status: "error",
        });
      } finally {
        setPortalActionLoading(false);
      }
    },
    [consultantId, fetchConsultant, fetchPortalData, selectedBusinessUnitId, toast, t]
  );

  const handleReviewDecision = useCallback(
    async (payload, reviewComment) => {
      const { type, id, decision } = payload;
      const comment = (reviewComment || "").trim();
      try {
        if (type === "activity") {
          if (decision === "approve") {
            await approveConsultantActivityReport(id, { reviewComment: comment }, {
              buId: selectedBusinessUnitId || undefined,
            });
          } else {
            await rejectConsultantActivityReport(id, { reviewComment: comment }, {
              buId: selectedBusinessUnitId || undefined,
            });
          }
        } else if (type === "leave") {
          if (decision === "approve") {
            await approveConsultantLeaveRequest(id, { reviewComment: comment }, {
              buId: selectedBusinessUnitId || undefined,
            });
          } else {
            await rejectConsultantLeaveRequest(id, { reviewComment: comment }, {
              buId: selectedBusinessUnitId || undefined,
            });
          }
        }
        toast({
          title: decision === "approve"
            ? t("pilotConsultants.detail.review.toasts.approved", { defaultValue: "Review approved" })
            : t("pilotConsultants.detail.review.toasts.rejected", { defaultValue: "Review rejected" }),
          status: "success",
        });
        fetchPortalData();
      } catch (error) {
        toast({
          title: t("pilotConsultants.detail.review.toasts.failedTitle", { defaultValue: "Review action failed" }),
          description: error?.response?.data?.detail || t("pilotConsultants.detail.review.toasts.failedDescription", { defaultValue: "Please try again." }),
          status: "error",
        });
      }
    },
    [fetchPortalData, selectedBusinessUnitId, toast, t]
  );

  const openReviewCommentModal = useCallback((payload) => {
    setPendingReview(payload);
    setReviewCommentText("");
    reviewCommentModal.onOpen();
  }, [reviewCommentModal]);

  const closeReviewCommentModal = useCallback(() => {
    reviewCommentModal.onClose();
    setPendingReview(null);
    setReviewCommentText("");
  }, [reviewCommentModal]);

  const handleReviewSubmit = useCallback(async () => {
    if (!pendingReview) return;
    setReviewSubmitLoading(true);
    try {
      await handleReviewDecision(pendingReview, reviewCommentText);
    } finally {
      setReviewSubmitLoading(false);
      closeReviewCommentModal();
    }
  }, [pendingReview, reviewCommentText, handleReviewDecision, closeReviewCommentModal]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isBUContextLoaded) return;
    fetchConsultant();
    fetchComments();
    fetchMentionableUsers();
  }, [isOpen, isBUContextLoaded, fetchConsultant, fetchComments, fetchMentionableUsers]);


  useEffect(() => {
    if (!isOpen) return;
    if (!isBUContextLoaded || !consultantId) return;
    fetchPortalData();
  }, [isOpen, isBUContextLoaded, consultantId, fetchPortalData]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isBUContextLoaded || !consultantId) return;
    fetchConsultantMatches();
  }, [isOpen, isBUContextLoaded, consultantId, fetchConsultantMatches]);

  const handleDeleteConsultant = async () => {
    try {
      const res = await deleteConsultant(consultantId, {
        buId: selectedBusinessUnitId || undefined
      });
      if (res?.hasMatches) {
        setDeleteWarning(res);
        return;
      }
      toast({
        title: t("pilotConsultants.detail.toasts.deleteConsultantSuccess"),
        status: "success"
      });
      onClose?.();
      navigate("/portal/asap-pilot/consultants");
    } catch (e) {
      toast({
        title: t("pilotConsultants.detail.toasts.deleteFailedTitle"),
        description:
          e?.response?.data?.message ||
          t("pilotConsultants.detail.toasts.deleteFailedDescription"),
        status: "error"
      });
    }
  };

  const handleConfirmDeleteConsultant = async () => {
    try {
      await confirmDeleteConsultant(consultantId, {
        buId: selectedBusinessUnitId || undefined
      });
      toast({
        title: t("pilotConsultants.detail.toasts.deleteConsultantSuccess"),
        status: "success"
      });
      setDeleteWarning(null);
      onClose?.();
      navigate("/portal/asap-pilot/consultants");
    } catch (e) {
      toast({
        title: t("pilotConsultants.detail.toasts.deleteFailedTitle"),
        description:
          e?.response?.data?.message ||
          t("pilotConsultants.detail.toasts.deleteFailedDescription"),
        status: "error"
      });
    }
  };

  const handleDownloadCv = async () => {
    if (!consultantId) return;
    try {
      const response = await downloadConsultantCv(consultantId, {
        buId: selectedBusinessUnitId || undefined
      });
      const blob = response.data;
      const dispo = response.headers["content-disposition"] || "";
      const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(dispo);
      const serverFilename = match ? match[1].replace(/["']/g, "") : null;
      const fallbackName = `${consultant?.name || "consultant"}_cv`;
      const finalName = serverFilename || fallbackName;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", finalName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to download consultant CV", e);
      toast({
        title: t("asapCV.toasts.downloadFailedTitle"),
        description: t("asapCV.toasts.downloadErrors.generic"),
        status: "error"
      });
    }
  };

  const parseSkills = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        return [];
      }
    }
    return [];
  };

  const skillsTechnical = parseSkills(
    consultant?.skillsTechnical || consultant?.skills_technical
  );
  const skillsFunctional = parseSkills(
    consultant?.skillsFunctional || consultant?.skills_functional
  );
  const skillsBusiness = parseSkills(
    consultant?.skillsBusiness || consultant?.skills_business
  );

  const minSalary =
    consultant?.minSalary != null
      ? consultant.minSalary
      : consultant?.min_salary != null
      ? consultant.min_salary
      : null;
  const maxSalary =
    consultant?.maxSalary != null
      ? consultant.maxSalary
      : consultant?.max_salary != null
      ? consultant.max_salary
      : null;
  const salaryRange =
    minSalary != null || maxSalary != null
      ? [minSalary, maxSalary]
          .filter((v) => v != null)
          .map((v) => v.toString())
          .join(" - ")
      : "-";
  const contractTypeValue = consultant?.contractType ?? consultant?.contract_type;
  const contractTypeLabel = contractTypeValue
    ? t(`pilotConsultantTracking.list.contractTypes.${contractTypeValue}`, {
        defaultValue: CONTRACT_TYPE_LABELS[contractTypeValue] || contractTypeValue
      })
    : "-";
  const employmentStartDateValue =
    consultant?.employmentStartDate ?? consultant?.employment_start_date;
  const employmentStartDateLabel = (() => {
    if (!employmentStartDateValue) return "-";
    const date = new Date(employmentStartDateValue);
    if (Number.isNaN(date.getTime())) return String(employmentStartDateValue);
    return date.toLocaleDateString();
  })();

  const safeClose = () => {
    onClose?.();
    if (navigateOnClose) {
      try {
        navigate("/portal/asap-pilot/consultants");
      } catch {
        // ignore
      }
    }
  };

  const hasCvDownload = Boolean(consultant?.cv_url || consultant?.cvUrl);
  const portalAccessStatus =
    consultant?.portalAccessStatus ?? consultant?.portal_access_status ?? "inactive";
  const portalStatusColor = PORTAL_ACCESS_STATUS_COLORS[portalAccessStatus] || "gray";
  const linkedUserEmail =
    consultant?.linkedUserEmail ?? consultant?.linked_user_email ?? "";
  const portalInvitedAt =
    consultant?.portalInvitedAt ?? consultant?.portal_invited_at ?? null;

  const showInviteButton =
    canManagePortalAccess &&
    (portalAccessStatus === "inactive" || portalAccessStatus === "disabled");
  const showResendButton =
    canManagePortalAccess && portalAccessStatus === "invited";
  const showDisableButton =
    canManagePortalAccess && portalAccessStatus === "active";

  const consultantStatusLabel = getStatusLabel(
    t,
    CONSULTANT_STATUS_LABEL_KEYS,
    CONSULTANT_STATUS_LABELS,
    consultant?.status
  );
  const staffingStatusValue =
    consultant?.staffingStatus || consultant?.staffing_status;
  const staffingStatusLabel = staffingStatusValue
    ? getStatusLabel(
        t,
        CONSULTANT_STAFFING_STATUS_LABEL_KEYS,
        CONSULTANT_STAFFING_STATUS_LABELS,
        staffingStatusValue
      )
    : null;

  // Header status badge: lifecycle status, with portal-access badge alongside.
  const headerName = consultant?.name || t("pilotConsultants.detail.titleFallback");

  // Portal-access action buttons (shared between Profile + Follow-up tabs).
  const portalActionButtons = (showInviteButton || showResendButton || showDisableButton) && (
    <HStack spacing="8px">
      {showInviteButton && (
        <Button
          size="sm"
          variant="primary"
          onClick={() => handlePortalAction("invite")}
          isLoading={portalActionLoading}
        >
          {t("pilotConsultants.detail.consultantPortal.inviteButton")}
        </Button>
      )}
      {showResendButton && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => handlePortalAction("resend")}
          isLoading={portalActionLoading}
          isDisabled={!consultant?.email}
        >
          {t("pilotConsultants.detail.consultantPortal.resendInviteButton")}
        </Button>
      )}
      {showDisableButton && (
        <Button
          size="sm"
          variant="secondary"
          color="portal.danger"
          borderColor="portal.danger"
          _hover={{ bg: "danger.50", borderColor: "portal.danger" }}
          onClick={() => handlePortalAction("disable")}
          isLoading={portalActionLoading}
          isDisabled={!linkedUserEmail}
        >
          {t("pilotConsultants.detail.consultantPortal.disableAccessButton")}
        </Button>
      )}
    </HStack>
  );

  // ---- Matches tab columns ----
  const matchColumns = [
    { key: "client", label: t("pilotNeeds.list.table.client"), width: "20%" },
    { key: "title", label: t("pilotNeeds.list.table.title") },
    { key: "score", label: t("pilotMatches.detail.table.score"), width: "90px" },
    { key: "workflow", label: t("pilotMatches.detail.table.workflowStatus", { defaultValue: "Workflow" }), width: "14%" },
    { key: "submission", label: t("pilotMatches.detail.submission.columnTitle", { defaultValue: "Submission" }), width: "18%" },
    { key: "positioned", label: t("pilotMatches.detail.table.positioned"), width: "12%" }
  ];

  const activityColumns = [
    { key: "period", label: t("pilotConsultants.detail.followUp.activity.period", { defaultValue: "Period" }), width: "16%" },
    { key: "days", label: t("pilotConsultants.detail.followUp.activity.days", { defaultValue: "Days" }), width: "14%" },
    { key: "status", label: t("pilotConsultants.detail.followUp.activity.status", { defaultValue: "Status" }), width: "16%" },
    { key: "review", label: t("pilotConsultants.detail.followUp.activity.review", { defaultValue: "Review" }) },
    { key: "actions", label: "", width: "140px", align: "right" }
  ];

  const leaveColumns = [
    { key: "type", label: t("pilotConsultants.detail.followUp.leave.type", { defaultValue: "Type" }), width: "16%" },
    { key: "dates", label: t("pilotConsultants.detail.followUp.leave.dates", { defaultValue: "Dates" }), width: "22%" },
    { key: "status", label: t("pilotConsultants.detail.followUp.leave.status", { defaultValue: "Status" }), width: "16%" },
    { key: "review", label: t("pilotConsultants.detail.followUp.leave.review", { defaultValue: "Review" }) },
    { key: "actions", label: "", width: "140px", align: "right" }
  ];

  const feedbackColumns = [
    { key: "client", label: t("pilotConsultants.detail.followUp.feedback.client", { defaultValue: "Client" }), width: "16%" },
    { key: "assignment", label: t("pilotConsultants.detail.followUp.feedback.assignment", { defaultValue: "Assignment" }), width: "18%" },
    { key: "cycle", label: t("pilotConsultants.detail.followUp.feedback.cycle", { defaultValue: "Cycle" }), width: "20%" },
    { key: "rating", label: t("pilotConsultants.detail.followUp.feedback.rating", { defaultValue: "Rating" }), width: "10%" },
    { key: "status", label: t("pilotConsultants.detail.followUp.feedback.status", { defaultValue: "Status" }), width: "12%" },
    { key: "comment", label: t("pilotConsultants.detail.followUp.feedback.comment", { defaultValue: "Comment" }) }
  ];

  const filteredMatches = useMemo(() => {
    return (matchFilter === "submitted"
      ? consultantMatches.filter(({ match: m }) =>
          (m.submissionStatus ?? m.submission_status) === "submitted_to_client"
        )
      : consultantMatches
    )
      .slice()
      .sort((a, b) => (b.match.score ?? 0) - (a.match.score ?? 0));
  }, [matchFilter, consultantMatches]);

  const lineTabSx = {
    "& [role=tablist]": {
      borderBottom: "1px solid",
      borderColor: "portal.line",
      gap: "4px"
    },
    "& [role=tab]": {
      fontSize: "13px",
      fontWeight: 700,
      color: "portal.muted",
      borderBottom: "2px solid transparent",
      borderRadius: 0,
      px: "12px",
      py: "10px",
      mb: "-1px"
    },
    "& [role=tab]:hover": {
      color: "portal.text"
    },
    "& [role=tab][aria-selected=true]": {
      color: "portal.primary",
      borderBottomColor: "portal.primary"
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={safeClose} size="6xl">
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX} pr="56px">
            <Flex align="center" justify="space-between" gap="12px" flexWrap="wrap">
              <HStack spacing="12px" minW={0}>
                <Heading
                  as="h2"
                  fontSize="17px"
                  fontWeight={800}
                  letterSpacing="-0.02em"
                  color="portal.text"
                  isTruncated
                  sx={{ marginBottom: "0 !important" }}
                >
                  {headerName}
                </Heading>
                {consultantStatusLabel && (
                  <StatusBadge
                    label={consultantStatusLabel}
                    colorScheme={CONSULTANT_LIFECYCLE_STATUS_COLORS[consultant?.status] || "gray"}
                  />
                )}
                <StatusBadge
                  label={t(
                    `pilotConsultants.detail.consultantPortal.statusLabels.${portalAccessStatus}`,
                    portalAccessStatus
                  )}
                  colorScheme={portalStatusColor}
                />
              </HStack>
              <HStack spacing="8px" flexShrink={0}>
                {hasCvDownload && (
                  <Button
                    leftIcon={<DownloadIcon />}
                    variant="secondary"
                    size="sm"
                    onClick={handleDownloadCv}
                  >
                    {t("pilotConsultants.detail.header.downloadCv", {
                      defaultValue: "Download CV"
                    })}
                  </Button>
                )}
                <Button
                  leftIcon={<EditIcon />}
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setEditingItem(consultant);
                    editModal.onOpen();
                  }}
                  isDisabled={!consultant}
                >
                  {t("pilotConsultants.detail.header.edit")}
                </Button>
                {canDelete && (
                  <Button
                    leftIcon={<DeleteIcon />}
                    variant="secondary"
                    size="sm"
                    color="portal.danger"
                    borderColor="portal.danger"
                    _hover={{ bg: "danger.50", borderColor: "portal.danger" }}
                    onClick={deleteConfirmDialog.onOpen}
                    isDisabled={!consultant}
                  >
                    {t("pilotConsultants.detail.header.delete")}
                  </Button>
                )}
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody bg="portal.bg" py="14px">
            <Tabs variant="unstyled" isLazy sx={lineTabSx}>
              <TabList>
                <Tab>{t("pilotConsultants.detail.tabs.profile", { defaultValue: "Profile" })}</Tab>
                <Tab>{t("pilotConsultants.detail.tabs.conversations", { defaultValue: "Conversations" })}</Tab>
                {hasCvModule && (
                  <Tab>{t("pilotConsultants.detail.tabs.cvPortfolio", { defaultValue: "CV Portfolio" })}</Tab>
                )}
                {hasSkillsModule && (
                  <Tab>{t("pilotConsultants.detail.tabs.asapSkills", { defaultValue: "ASAP SKILLS" })}</Tab>
                )}
                <Tab>{t("pilotConsultants.detail.tabs.assignments", { defaultValue: "Assignments" })}</Tab>
                <Tab>{t("pilotConsultants.detail.tabs.matches")}</Tab>
                <Tab>{t("pilotConsultants.detail.tabs.candidateFollowUp", { defaultValue: "Candidate Follow-up" })}</Tab>
                <Tab>{t("pilotConsultants.detail.tabs.comments")}</Tab>
              </TabList>

              <TabPanels>
                {/* Profile */}
                <TabPanel px={0} pt="14px">
                  {loadingConsultant ? (
                    <Panel title={t("pilotConsultants.detail.tabs.profile", { defaultValue: "Profile" })}>
                      <Flex align="center" justify="center" py="40px">
                        <Spinner color="portal.primary" />
                      </Flex>
                    </Panel>
                  ) : !consultant ? (
                    <Panel>
                      <EmptyState title={t("pilotConsultants.detail.notFound")} />
                    </Panel>
                  ) : (
                    <VStack align="stretch" spacing="10px">
                      <Panel title={t("pilotConsultants.detail.tabs.profile", { defaultValue: "Profile" })}>
                        <Box p="12px">
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="16px" spacingY="12px">
                            <Box>
                              <FieldLabel>{t("pilotConsultants.detail.sections.title")}</FieldLabel>
                              <FieldValue>{consultant.title || "-"}</FieldValue>
                            </Box>
                            <Box>
                              <FieldLabel>{t("pilotConsultants.detail.sections.email")}</FieldLabel>
                              <FieldValue>{consultant.email || "-"}</FieldValue>
                            </Box>
                            <Box>
                              <FieldLabel>{t("pilotConsultants.detail.sections.phone")}</FieldLabel>
                              <FieldValue>{consultant.phone || "-"}</FieldValue>
                            </Box>
                            <Box>
                              <FieldLabel>{t("pilotConsultants.detail.sections.location")}</FieldLabel>
                              <FieldValue>{consultant.location || "-"}</FieldValue>
                            </Box>
                            <Box>
                              <FieldLabel>{t("pilotConsultants.detail.sections.availability")}</FieldLabel>
                              <FieldValue>{consultant.availability || "-"}</FieldValue>
                            </Box>
                            <Box>
                              <FieldLabel>{t("pilotConsultants.detail.sections.contractType")}</FieldLabel>
                              <FieldValue>{contractTypeLabel}</FieldValue>
                            </Box>
                            <Box>
                              <FieldLabel>{t("pilotConsultants.detail.sections.employmentStartDate")}</FieldLabel>
                              <FieldValue>{employmentStartDateLabel}</FieldValue>
                            </Box>
                            <Box>
                              <FieldLabel>{t("pilotConsultants.detail.sections.status")}</FieldLabel>
                              {consultantStatusLabel ? (
                                <StatusBadge
                                  label={consultantStatusLabel}
                                  colorScheme={CONSULTANT_LIFECYCLE_STATUS_COLORS[consultant.status] || "gray"}
                                />
                              ) : (
                                <FieldValue>-</FieldValue>
                              )}
                            </Box>
                            <Box>
                              <FieldLabel>{t("pilotConsultants.detail.sections.staffingStatus")}</FieldLabel>
                              {staffingStatusLabel ? (
                                <StatusBadge
                                  label={staffingStatusLabel}
                                  colorScheme={CONSULTANT_STAFFING_STATUS_COLORS[staffingStatusValue] || "gray"}
                                />
                              ) : (
                                <FieldValue>-</FieldValue>
                              )}
                            </Box>
                            <Box>
                              <FieldLabel>{t("pilotConsultants.detail.sections.salaryRange")}</FieldLabel>
                              <FieldValue>{salaryRange}</FieldValue>
                            </Box>
                          </SimpleGrid>

                          {consultant.description && (
                            <>
                              <Divider my="14px" borderColor="portal.line" />
                              <Box>
                                <FieldLabel>{t("pilotConsultants.detail.sections.description")}</FieldLabel>
                                <Text
                                  fontSize="13px"
                                  color="portal.text"
                                  whiteSpace="pre-wrap"
                                  sx={{ marginBottom: "0 !important" }}
                                >
                                  {consultant.description}
                                </Text>
                              </Box>
                            </>
                          )}

                          {(skillsTechnical.length > 0 ||
                            skillsFunctional.length > 0 ||
                            skillsBusiness.length > 0) && (
                            <>
                              <Divider my="14px" borderColor="portal.line" />
                              {skillsTechnical.length > 0 && (
                                <Box mb="12px">
                                  <FieldLabel>{t("pilotConsultants.detail.sections.technicalSkills")}</FieldLabel>
                                  <HStack wrap="wrap" spacing="6px">
                                    {skillsTechnical.map((skill, idx) => (
                                      <SkillTag key={`tech-${idx}`} colorScheme="blue">{skill}</SkillTag>
                                    ))}
                                  </HStack>
                                </Box>
                              )}
                              {skillsFunctional.length > 0 && (
                                <Box mb="12px">
                                  <FieldLabel>{t("pilotConsultants.detail.sections.functionalSkills")}</FieldLabel>
                                  <HStack wrap="wrap" spacing="6px">
                                    {skillsFunctional.map((skill, idx) => (
                                      <SkillTag key={`func-${idx}`} colorScheme="orange">{skill}</SkillTag>
                                    ))}
                                  </HStack>
                                </Box>
                              )}
                              {skillsBusiness.length > 0 && (
                                <Box mb="12px">
                                  <FieldLabel>{t("pilotConsultants.detail.sections.businessSkills")}</FieldLabel>
                                  <HStack wrap="wrap" spacing="6px">
                                    {skillsBusiness.map((skill, idx) => (
                                      <SkillTag key={`biz-${idx}`} colorScheme="green">{skill}</SkillTag>
                                    ))}
                                  </HStack>
                                </Box>
                              )}
                            </>
                          )}
                        </Box>
                      </Panel>

                      {/* Portal access */}
                      <Panel title={t("pilotConsultants.detail.consultantPortal.sectionLabel")}>
                        <Box p="12px">
                          <Flex justify="space-between" align="center" wrap="wrap" gap="12px">
                            <Box>
                              <HStack spacing="8px">
                                <StatusBadge
                                  label={t(
                                    `pilotConsultants.detail.consultantPortal.statusLabels.${portalAccessStatus}`,
                                    portalAccessStatus
                                  )}
                                  colorScheme={portalStatusColor}
                                />
                                <Text fontSize="13px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                                  {linkedUserEmail || consultant.email || "-"}
                                </Text>
                              </HStack>
                              <Text fontSize="12px" color="portal.muted" mt="6px" sx={{ marginBottom: "0 !important" }}>
                                {t("pilotConsultants.detail.consultantPortal.lastInvite", {
                                  date: portalInvitedAt ? new Date(portalInvitedAt).toLocaleString() : "-",
                                })}
                              </Text>
                            </Box>
                            {portalActionButtons}
                          </Flex>
                        </Box>
                      </Panel>
                    </VStack>
                  )}
                </TabPanel>

                {/* Conversations */}
                <TabPanel px={0} pt="14px">
                  <Panel>
                    <Box p="12px">
                      <ConsultantConversationsTab consultantId={consultantId} />
                    </Box>
                  </Panel>
                </TabPanel>

                {/* Skills Portfolio (replaces CV tab) */}
                {hasCvModule && (
                  <TabPanel px={0} pt="14px">
                    <Panel>
                      <Box p="12px">
                        <ConsultantSkillsPortfolioTab
                          consultantId={consultantId}
                          consultantName={consultant?.name}
                          hasCv={hasCvDownload}
                        />
                      </Box>
                    </Panel>
                  </TabPanel>
                )}

                {/* ASAP SKILLS (replaces Assessment tab) */}
                {hasSkillsModule && (
                  <TabPanel px={0} pt="14px">
                    <Panel>
                      <Box p="12px">
                        <ConsultantSkillsTab consultantId={consultantId} consultantName={consultant?.name} consultantEmail={consultant?.email} />
                      </Box>
                    </Panel>
                  </TabPanel>
                )}

                {/* Assignments */}
                <TabPanel px={0} pt="14px">
                  <Panel>
                    <Box p="12px">
                      <ConsultantAssignmentsTab consultantId={consultantId} />
                    </Box>
                  </Panel>
                </TabPanel>

                {/* Matches */}
                <TabPanel px={0} pt="14px">
                  <Panel
                    title={t("pilotConsultants.detail.matches.title")}
                    count={loadingMatches ? undefined : filteredMatches.length}
                    actions={
                      <HStack spacing="8px">
                        {(consultant?.lastReverseMatchAt || consultant?.last_reverse_match_at) && (
                          <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                            {t("pilotConsultants.detail.matches.lastGenerated", { defaultValue: "Last generated" })}:{" "}
                            {new Date(consultant.lastReverseMatchAt || consultant.last_reverse_match_at).toLocaleDateString()}
                          </Text>
                        )}
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={handleGenerateMatches}
                          isLoading={generatingMatches}
                        >
                          {t("pilotConsultants.detail.matches.generateMatches", { defaultValue: "Generate matches" })}
                        </Button>
                      </HStack>
                    }
                    filters={
                      <Select
                        value={matchFilter}
                        onChange={(e) => setMatchFilter(e.target.value)}
                        size="sm"
                        maxW="220px"
                        borderRadius="10px"
                      >
                        <option value="all">{t("pilotConsultants.detail.matches.filterAll", { defaultValue: "All matches" })}</option>
                        <option value="submitted">{t("pilotConsultants.detail.matches.filterSubmitted", { defaultValue: "Submitted to client" })}</option>
                      </Select>
                    }
                  >
                    <DataTable
                      columns={matchColumns}
                      rows={filteredMatches}
                      loading={loadingMatches}
                      emptyState={
                        <EmptyState
                          icon={<FaSitemap />}
                          title={t("pilotConsultants.detail.matches.noMatches")}
                        />
                      }
                      renderRow={({ match: m, need }) => (
                        <Tr
                          key={m.id}
                          cursor="pointer"
                          onClick={() => {
                            setSelectedMatchId(m.id);
                            setSelectedMatch(m);
                            setSelectedNeed(need);
                            matchDetailModal.onOpen();
                          }}
                        >
                          <Td>{need?.clientName || need?.client_name || "-"}</Td>
                          <Td fontWeight="700">{need?.title || "-"}</Td>
                          <Td>
                            {m.score != null ? (
                              <Badge colorScheme={m.score >= 70 ? "green" : m.score >= 40 ? "yellow" : "red"}>
                                {m.score}%
                              </Badge>
                            ) : "-"}
                          </Td>
                          <Td>
                            {(m.workflowStatus ?? m.workflow_status) ? (
                              <Badge
                                colorScheme={
                                  { VALIDATED: "blue", SENT_TO_CLIENT: "purple", INTERVIEW: "orange", AWAITING_FEEDBACK: "yellow", WON: "green", WITHDRAWN: "gray", REJECTED: "red" }[m.workflowStatus ?? m.workflow_status] || "gray"
                                }
                                variant="subtle"
                              >
                                {m.workflowStatus ?? m.workflow_status}
                              </Badge>
                            ) : "-"}
                          </Td>
                          <Td whiteSpace="normal">
                            <Badge
                              colorScheme={getSubmissionBadgeColor(getMatchSubmissionStatus(m))}
                              variant="subtle"
                            >
                              {getSubmissionStatusLabel(getMatchSubmissionStatus(m))}
                            </Badge>
                            {formatSubmittedAt(m?.submittedAt ?? m?.submitted_at) && (
                              <Text fontSize="11px" color="portal.muted" mt="2px" sx={{ marginBottom: "0 !important" }}>
                                {formatSubmittedAt(m?.submittedAt ?? m?.submitted_at)}
                              </Text>
                            )}
                          </Td>
                          <Td>
                            {isMatchPositioned(m)
                              ? t("pilotMatches.detail.positioned.yes")
                              : t("pilotMatches.detail.positioned.no")}
                          </Td>
                        </Tr>
                      )}
                    />
                  </Panel>
                </TabPanel>

                {/* Candidate Follow-up */}
                <TabPanel px={0} pt="14px">
                  <ConsultantFollowUpTab consultantId={consultantId}>
                    <VStack align="stretch" spacing="10px">
                      {/* Portal access */}
                      <Panel title={t("pilotConsultants.detail.consultantPortal.portalAccessHeading")}>
                        <Box p="12px">
                          <Flex justify="space-between" align="center" wrap="wrap" gap="12px">
                            <HStack spacing="8px">
                              <StatusBadge
                                label={t(
                                  `pilotConsultants.detail.consultantPortal.statusLabels.${portalAccessStatus}`,
                                  portalAccessStatus
                                )}
                                colorScheme={portalStatusColor}
                              />
                              <Text fontSize="13px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                                {linkedUserEmail || consultant?.email || "-"}
                              </Text>
                            </HStack>
                            {portalActionButtons}
                          </Flex>
                        </Box>
                      </Panel>

                      {/* Activity reports */}
                      <Panel
                        title={t("pilotConsultants.detail.followUp.activity.title", { defaultValue: "Activity reports" })}
                        count={portalLoading ? undefined : activityReports.length}
                      >
                        <DataTable
                          columns={activityColumns}
                          rows={activityReports}
                          loading={portalLoading}
                          emptyState={
                            <EmptyState
                              icon={<FaRegFileAlt />}
                              title={t("pilotConsultants.detail.followUp.activity.empty", { defaultValue: "No activity reports yet." })}
                            />
                          }
                          renderRow={(item) => (
                            <Tr key={item.id}>
                              <Td>{`${item.periodYear}-${String(item.periodMonth).padStart(2, "0")}`}</Td>
                              <Td>{`${item.billableDays} / ${item.nonBillableDays}`}</Td>
                              <Td>
                                <Badge colorScheme={item.status === "approved" ? "green" : item.status === "submitted" ? "blue" : item.status === "rejected" ? "red" : "gray"}>
                                  {item.status}
                                </Badge>
                              </Td>
                              <Td whiteSpace="normal">{item.reviewComment || "-"}</Td>
                              <Td textAlign="right">
                                {canReviewActivityAndLeave && item.status === "submitted" && (
                                  <HStack justify="flex-end" spacing="6px">
                                    <Button
                                      size="xs"
                                      colorScheme="green"
                                      variant="ghost"
                                      onClick={() => openReviewCommentModal({ type: "activity", id: item.id, decision: "approve" })}
                                    >
                                      {t("pilotConsultants.detail.review.approve", { defaultValue: "Approve" })}
                                    </Button>
                                    <Button
                                      size="xs"
                                      colorScheme="red"
                                      variant="ghost"
                                      onClick={() => openReviewCommentModal({ type: "activity", id: item.id, decision: "reject" })}
                                    >
                                      {t("pilotConsultants.detail.review.reject", { defaultValue: "Reject" })}
                                    </Button>
                                  </HStack>
                                )}
                              </Td>
                            </Tr>
                          )}
                        />
                      </Panel>

                      {/* Leave requests */}
                      <Panel
                        title={t("pilotConsultants.detail.followUp.leave.title", { defaultValue: "Leave requests" })}
                        count={portalLoading ? undefined : leaveRequests.length}
                      >
                        <DataTable
                          columns={leaveColumns}
                          rows={leaveRequests}
                          loading={portalLoading}
                          emptyState={
                            <EmptyState
                              icon={<FaRegFileAlt />}
                              title={t("pilotConsultants.detail.followUp.leave.empty", { defaultValue: "No leave requests yet." })}
                            />
                          }
                          renderRow={(item) => (
                            <Tr key={item.id}>
                              <Td>{item.leaveType}</Td>
                              <Td>{`${item.startDate} -> ${item.endDate}`}</Td>
                              <Td>
                                <Badge colorScheme={item.status === "approved" ? "green" : item.status === "submitted" ? "blue" : item.status === "rejected" ? "red" : item.status === "cancelled" ? "orange" : "gray"}>
                                  {item.status}
                                </Badge>
                              </Td>
                              <Td whiteSpace="normal">{item.reviewComment || "-"}</Td>
                              <Td textAlign="right">
                                {canReviewActivityAndLeave && item.status === "submitted" && (
                                  <HStack justify="flex-end" spacing="6px">
                                    <Button
                                      size="xs"
                                      colorScheme="green"
                                      variant="ghost"
                                      onClick={() => openReviewCommentModal({ type: "leave", id: item.id, decision: "approve" })}
                                    >
                                      {t("pilotConsultants.detail.review.approve", { defaultValue: "Approve" })}
                                    </Button>
                                    <Button
                                      size="xs"
                                      colorScheme="red"
                                      variant="ghost"
                                      onClick={() => openReviewCommentModal({ type: "leave", id: item.id, decision: "reject" })}
                                    >
                                      {t("pilotConsultants.detail.review.reject", { defaultValue: "Reject" })}
                                    </Button>
                                  </HStack>
                                )}
                              </Td>
                            </Tr>
                          )}
                        />
                      </Panel>

                      {/* Assignment feedback */}
                      <Panel
                        title={t("pilotConsultants.detail.followUp.feedback.title", { defaultValue: "Assignment feedback" })}
                        count={portalLoading ? undefined : assignmentFeedback.length}
                        footer={
                          !canViewAssignmentFeedback ? (
                            <Box px="12px" py="10px" borderTopWidth="1px" borderColor="portal.line">
                              <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                                {t("pilotConsultants.detail.followUp.feedback.noPermission", { defaultValue: "You do not have permission to review assignment feedback." })}
                              </Text>
                            </Box>
                          ) : undefined
                        }
                      >
                        <DataTable
                          columns={feedbackColumns}
                          rows={assignmentFeedback}
                          loading={portalLoading}
                          emptyState={
                            <EmptyState
                              icon={<FaRegFileAlt />}
                              title={t("pilotConsultants.detail.followUp.feedback.empty", { defaultValue: "No assignment feedback yet." })}
                            />
                          }
                          renderRow={(item) => (
                            <Tr key={item.id}>
                              <Td>{item.assignment?.clientName || "-"}</Td>
                              <Td>{item.assignment?.needTitle || "-"}</Td>
                              <Td>{`${item.cycleStartDate} -> ${item.cycleEndDate}`}</Td>
                              <Td>{item.rating || "-"}</Td>
                              <Td>
                                <Badge colorScheme={item.status === "submitted" ? "green" : "gray"}>
                                  {item.status}
                                </Badge>
                              </Td>
                              <Td whiteSpace="normal">{item.comment || "-"}</Td>
                            </Tr>
                          )}
                        />
                      </Panel>
                    </VStack>
                  </ConsultantFollowUpTab>
                </TabPanel>

                {/* Comments */}
                <TabPanel px={0} pt="14px">
                  <Panel title={t("pilotConsultants.detail.comments.title")}>
                    <Box p="12px">
                      <ThreadedComments
                        comments={comments}
                        isLoading={loadingComments}
                        emptyLabel={t("pilotConsultants.detail.comments.noComments")}
                        submitLabel={t("pilotConsultants.detail.comments.actions.submit")}
                        replyLabel={t("pilotConsultants.detail.comments.actions.reply")}
                        cancelLabel={t("pilotConsultants.detail.comments.actions.cancel")}
                        placeholder={t("pilotConsultants.detail.comments.placeholders.new")}
                        replyPlaceholder={t("pilotConsultants.detail.comments.placeholders.reply")}
                        mentionableUsers={mentionableUsers}
                        onCreate={async ({ text, parentCommentId }) => {
                          await addConsultantComment(
                            consultantId,
                            text,
                            { buId: selectedBusinessUnitId || undefined },
                            parentCommentId
                          );
                        }}
                        onReload={fetchComments}
                        onCreateSuccess={() =>
                          toast({
                            title: t("pilotConsultants.detail.toasts.commentAdded"),
                            status: "success"
                          })
                        }
                        onCreateError={(e) =>
                          toast({
                            title: t("pilotConsultants.detail.toasts.commentFailedTitle"),
                            description:
                              e?.response?.data?.message ||
                              t("pilotConsultants.detail.toasts.commentFailedDescription"),
                            status: "error"
                          })
                        }
                      />
                    </Box>
                  </Panel>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" onClick={safeClose}>
              {t("pilotConsultants.detail.close")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={reviewCommentModal.isOpen}
        onClose={closeReviewCommentModal}
        size="md"
        isCentered
      >
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            {pendingReview?.decision === "approve"
              ? t("pilotConsultants.detail.review.approveTitle", { defaultValue: "Approve request" })
              : t("pilotConsultants.detail.review.rejectTitle", { defaultValue: "Reject request" })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="16px">
            <Text fontSize="13px" color="portal.muted" mb="8px" sx={{ marginBottom: "8px !important" }}>
              {t("pilotConsultants.detail.review.commentHint", { defaultValue: "Optionally add a comment or note for this review." })}
            </Text>
            <Textarea
              value={reviewCommentText}
              onChange={(e) => setReviewCommentText(e.target.value)}
              placeholder={t("pilotConsultants.detail.review.commentPlaceholder", { defaultValue: "Comment (optional)" })}
              size="sm"
              fontSize="13px"
              minH="80px"
              resize="vertical"
            />
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" onClick={closeReviewCommentModal} isDisabled={reviewSubmitLoading}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              size="sm"
              colorScheme={pendingReview?.decision === "approve" ? "green" : "red"}
              onClick={handleReviewSubmit}
              isLoading={reviewSubmitLoading}
            >
              {pendingReview?.decision === "approve"
                ? t("pilotConsultants.detail.review.approve", { defaultValue: "Approve" })
                : t("pilotConsultants.detail.review.reject", { defaultValue: "Reject" })}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConsultantFormModal
        isOpen={editModal.isOpen}
        onClose={() => {
          setEditingItem(null);
          editModal.onClose();
        }}
        initialData={editingItem}
        onSuccess={() => {
          fetchConsultant();
        }}
      />

      <ConsultantAssessmentModal
        isOpen={assessmentModal.isOpen}
        onClose={assessmentModal.onClose}
        consultantId={consultantId}
        consultant={consultant}
        onSuccess={() => {}}
      />

      <MatchDetailModal
        isOpen={matchDetailModal.isOpen}
        onClose={() => {
          setSelectedMatchId(null);
          setSelectedMatch(null);
          setSelectedNeed(null);
          matchDetailModal.onClose();
          fetchConsultantMatches();
        }}
        matchId={selectedMatchId}
        initialMatch={selectedMatch}
        initialNeed={selectedNeed}
      />
      <ConfirmDialog
        isOpen={deleteConfirmDialog.isOpen && !deleteWarning}
        onClose={deleteConfirmDialog.onClose}
        onConfirm={handleDeleteConsultant}
        title={t("pilotConsultants.detail.confirmDeleteTitle", { defaultValue: "Delete consultant?" })}
        body={t("pilotConsultants.detail.confirmDeleteConsultant")}
        confirmLabel={t("common.delete", { defaultValue: "Delete" })}
      />
      <ConfirmDialog
        isOpen={!!deleteWarning}
        onClose={() => { setDeleteWarning(null); deleteConfirmDialog.onClose(); }}
        onConfirm={handleConfirmDeleteConsultant}
        title={t("pilotConsultants.detail.confirmDeleteTitle", { defaultValue: "Delete consultant?" })}
        body={t("pilotConsultants.detail.confirmDeleteWithMatches", {
          count: deleteWarning?.matchCount ?? 0,
          defaultValue: `This consultant has ${deleteWarning?.matchCount ?? 0} match(es). Deleting will also remove all associated matches. This action cannot be undone.`
        })}
        confirmLabel={t("common.delete", { defaultValue: "Delete" })}
      />
    </>
  );
};

export default ConsultantDetailModal;
